import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../common/state/store";
import { installApkFromUrl } from "../utils/adbInstaller";
import { installViaAgent, launchViaAgent, uninstallViaAgent } from "../utils/agentInstaller";
import { launchApp } from "../utils/appLauncher";
import { SPATIAL_STUDIO_PACKAGE_NAME, SPATIAL_STUDIO_AGENT_ID } from "../constants";
import { useSpatialStudioStatus } from "../hooks/useSpatialStudioStatus";
import { useAgentSpatialStudioStatus } from "../hooks/useAgentSpatialStudioStatus";
import type { AgentInfo } from "../hooks/useAgentConnection";
import type { SpatialStudioState } from "../hooks/useSpatialStudioStatus";
import InstallProgressModal from "./InstallProgressModal";
import UninstallButton from "./UninstallButton";
import LaunchButton from "./LaunchButton";
import AgentUninstallButton from "./AgentUninstallButton";
import { CheckIcon, WarningCircleIcon, DownloadArrowIcon, CopyIcon } from "./icons";
import spatialStudioIcon from "../../../assets/spatial_studio_icon.png";

type InstallMode = "adb" | "agent";

interface Props {
  mode?: InstallMode;
  agentInfo?: AgentInfo | null;
  onRemoveApp?: (appId: string) => void;
}

export default function SpatialStudioSection({ mode = "adb", agentInfo, onRemoveApp }: Props) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  // Use the appropriate hook based on mode
  const adbState = useSpatialStudioStatus();
  const agentState = useAgentSpatialStudioStatus(mode === "agent" ? agentInfo ?? null : null);
  const state: SpatialStudioState = mode === "agent" ? agentState : adbState;

  const { status, installedVersion, latestVersion, s3Url, refresh } = state;
  const [showModal, setShowModal] = useState(false);
  const [copiedPath, setCopiedPath] = useState(false);

  const installedApp = agentInfo?.installedApps.find((a) => a.id === SPATIAL_STUDIO_AGENT_ID);

  const handleCopyPath = async () => {
    if (!installedApp?.installPath) return;
    try {
      await navigator.clipboard.writeText(installedApp.installPath);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = installedApp.installPath;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopiedPath(true);
    setTimeout(() => setCopiedPath(false), 2000);
  };

  const handleInstall = async () => {
    if (!s3Url || !latestVersion) return;
    setShowModal(true);

    if (mode === "agent") {
      await installViaAgent(
        {
          appId: SPATIAL_STUDIO_AGENT_ID,
          displayName: "Spatial Studio",
          packageName: SPATIAL_STUDIO_PACKAGE_NAME,
          s3Url,
          version: latestVersion,
          fileName: `SpatialStudio_${latestVersion}.zip`,
        },
        dispatch,
      ).catch(() => {
        // Error already dispatched to Redux by installViaAgent
      });
    } else {
      const fileName = `SpatialStudio_${latestVersion}.zip`;
      await installApkFromUrl(s3Url, fileName, undefined, dispatch, SPATIAL_STUDIO_PACKAGE_NAME);
    }
  };

  if (status === "not_available") return null;

  if (status === "loading") {
    return (
      <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-200" />
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded" />
              <div className="h-3 w-44 bg-gray-100 rounded mt-1.5" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-4 w-20 bg-gray-100 rounded" />
            <div className="h-6 w-24 bg-gray-200 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  const isInstalled = status === "up_to_date" || status === "update_available";

  return (
    <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={spatialStudioIcon} alt="Spatial Studio" className="w-10 h-10 rounded-lg" />
          <div>
            <h3 className="text-base font-semibold text-gray-800">{t("spatialStudio.title")}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{SPATIAL_STUDIO_PACKAGE_NAME}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Version info */}
          <div className="text-right text-sm">
            {isInstalled && <span className="text-gray-500">V{installedVersion}</span>}
            {latestVersion && (
              <span className="text-gray-400 ml-2 text-xs">
                ({t("spatialStudio.latest")}: V{latestVersion})
              </span>
            )}
          </div>

          {/* Status badge */}
          {status === "up_to_date" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-50 text-green-600 border border-green-200">
              <CheckIcon className="w-3 h-3" />
              {t("deviceManager.upToDate")}
            </span>
          )}
          {status === "update_available" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-600 border border-orange-200">
              <WarningCircleIcon className="w-3 h-3" />
              {t("deviceManager.outdated")}
            </span>
          )}
          {status === "not_installed" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500 border border-gray-200">
              {t("spatialStudio.notInstalled")}
            </span>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isInstalled && (
              <LaunchButton
                onLaunch={() =>
                  mode === "agent"
                    ? launchViaAgent(SPATIAL_STUDIO_AGENT_ID)
                    : launchApp(SPATIAL_STUDIO_PACKAGE_NAME)
                }
              />
            )}

            {(status === "not_installed" || status === "update_available") && s3Url && (
              <button
                onClick={handleInstall}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-white bg-green-500 hover:opacity-90 transition-colors"
              >
                <DownloadArrowIcon />
                {status === "not_installed"
                  ? t("spatialStudio.install")
                  : t("deviceInstaller.update")}{" "}
                V{latestVersion}
              </button>
            )}

            {/* Copy install path (agent only) */}
            {isInstalled && mode === "agent" && installedApp?.installPath && (
              <button
                onClick={handleCopyPath}
                className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors border ${
                  copiedPath
                    ? "bg-green-50 text-green-500 border-green-200"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border-gray-200"
                }`}
                title={copiedPath ? t("desktopAgent.pathCopied") : t("desktopAgent.copyPath")}
              >
                {copiedPath ? (
                  <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                  <CopyIcon className="w-3.5 h-3.5" />
                )}
              </button>
            )}

            {isInstalled &&
              (mode === "agent" ? (
                <AgentUninstallButton
                  onUninstall={() => uninstallViaAgent(SPATIAL_STUDIO_AGENT_ID)}
                  onSuccess={() => {
                    onRemoveApp?.(SPATIAL_STUDIO_AGENT_ID);
                    refresh();
                  }}
                />
              ) : (
                <UninstallButton
                  packageName={SPATIAL_STUDIO_PACKAGE_NAME}
                  confirmBeforeUninstall
                  onUninstalled={refresh}
                />
              ))}
          </div>
        </div>
      </div>

      {showModal && <InstallProgressModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
